import { create } from 'zustand';
import { showError } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import type { ModuleKey } from '../../shell/session/store/sessionTypes';
import {
  fetchEmployees as fetchEmployeesApi,
  fetchSelfOwnerMembership,
  setEmployeeBranchPermissions,
  updateEmployeePermission as updateEmployeePermissionApi,
} from '../../team/application/employeesApi';
import { mapTeamAccessError } from '../../team/application/mapTeamAccessError';
import { EMPLOYEES_CACHE_MS } from '../../shell/session/sessionCacheTtl';
import type {
  EmployeePermission,
  EmployeeRecord,
  PermissionTriple,
  TogglePermissionField,
  PermissionLevel,
} from '../domain/employeeTypes';

export type {
  EmployeePermission,
  EmployeeRecord,
  PermissionTriple,
  TogglePermissionField,
  PermissionLevel,
} from '../domain/employeeTypes';

export function computeToggledPermission(
  current: PermissionTriple,
  field: TogglePermissionField
): PermissionTriple {
  if (field === 'canView') {
    const canView = !current.canView;
    return {
      canView,
      canEdit: canView ? current.canEdit : false,
      canVoid: canView ? current.canVoid : false,
      canDiscount: canView ? current.canDiscount : false,
      canManageAgenda: canView ? current.canManageAgenda : false,
      canViewOthers: canView ? current.canViewOthers : false,
      actions: canView ? current.actions : {},
    };
  }
  if (field === 'canEdit') {
    const canEdit = !current.canEdit;
    return {
      canView: canEdit ? true : current.canView,
      canEdit,
      canVoid: canEdit ? current.canVoid : false,
      canDiscount: canEdit ? current.canDiscount : false,
      canManageAgenda: canEdit ? current.canManageAgenda : false,
      canViewOthers: current.canViewOthers,
      actions: current.actions,
    };
  }
  if (field === 'canVoid') {
    const canVoid = !current.canVoid;
    return {
      canView: canVoid ? true : current.canView,
      canEdit: canVoid ? true : current.canEdit,
      canVoid,
      canDiscount: current.canDiscount,
      canManageAgenda: current.canManageAgenda,
      canViewOthers: current.canViewOthers,
      actions: current.actions,
    };
  }
  if (field === 'canManageAgenda') {
    const canManageAgenda = !current.canManageAgenda;
    return {
      ...current,
      canView: canManageAgenda ? true : current.canView,
      canEdit: canManageAgenda ? true : current.canEdit,
      canManageAgenda,
    };
  }
  if (field === 'canViewOthers') {
    const canViewOthers = !(current.canViewOthers ?? true);
    return {
      ...current,
      canView: canViewOthers ? true : current.canView,
      canViewOthers,
    };
  }
  const canDiscount = !current.canDiscount;
  return {
    canView: canDiscount ? true : current.canView,
    canEdit: canDiscount ? true : current.canEdit,
    canVoid: current.canVoid,
    canDiscount,
    canManageAgenda: current.canManageAgenda,
    canViewOthers: current.canViewOthers,
    actions: current.actions,
  };
}

export function applyPermissionToEmployees(
  employees: EmployeeRecord[],
  employeeId: string,
  branchId: string,
  module: ModuleKey,
  values: PermissionTriple
): EmployeeRecord[] {
  return employees.map((employee) => {
    if (employee.id !== employeeId) return employee;
    return {
      ...employee,
      branchAssignments: employee.branchAssignments.map((assignment) => {
        if (assignment.branchId !== branchId) return assignment;
        return {
          ...assignment,
          permissions: assignment.permissions.map((permission) => {
            if (permission.module !== module) return permission;
            return {
              ...permission,
              canView: values.canView,
              canEdit: values.canEdit,
              canVoid: values.canVoid,
              canDiscount: values.canDiscount,
              canManageAgenda: values.canManageAgenda,
              canViewOthers: values.canViewOthers ?? permission.canViewOthers ?? true,
              actions: values.actions ?? permission.actions ?? {},
            };
          }),
        };
      }),
    };
  });
}

export function readPermissionTriple(
  employees: EmployeeRecord[],
  employeeId: string,
  branchId: string,
  module: ModuleKey
): PermissionTriple {
  const perm = employees
    .find((e) => e.id === employeeId)
    ?.branchAssignments.find((a) => a.branchId === branchId)
    ?.permissions.find((p) => p.module === module);
  return {
    canView: perm?.canView ?? false,
    canEdit: perm?.canEdit ?? false,
    canVoid: perm?.canVoid ?? false,
    canDiscount: perm?.canDiscount ?? false,
    canManageAgenda: perm?.canManageAgenda ?? false,
    canViewOthers: perm?.canViewOthers ?? true,
    actions: perm?.actions ?? {},
  };
}

/**
 * Empleados + la membresía owner propia primero (para pickers de atribución:
 * "quién vendió/llevó/operó"). Team NO usa esto — su lista es solo invitados.
 */
export function withSelfOwner(
  employees: EmployeeRecord[],
  selfOwner: EmployeeRecord | null,
): EmployeeRecord[] {
  if (!selfOwner) return employees;
  if (employees.some((e) => e.id === selfOwner.id || e.authUserId === selfOwner.authUserId)) {
    return employees;
  }
  return [selfOwner, ...employees];
}

const EMPLOYEES_LOAD_ERROR = 'No se pudieron cargar los empleados. Revisá tu conexión o probá otra vez.';

let loadEmployeesInFlight: Promise<void> | null = null;
let employeesFetchedAt = 0;

/** Reset in-flight state and cache timestamp (used on session reset / logout). */
export function resetEmployeesLoadState(): void {
  loadEmployeesInFlight = null;
  employeesFetchedAt = 0;
}

export function permissionTripleFromLevel(
  level: PermissionLevel,
  preserve?: Pick<PermissionTriple, 'canManageAgenda' | 'canViewOthers' | 'actions'>
): PermissionTriple {
  const extras = {
    canManageAgenda: preserve?.canManageAgenda ?? false,
    canViewOthers: preserve?.canViewOthers ?? true,
    actions: preserve?.actions ?? {},
  };
  switch (level) {
    case 'view':
      return { canView: true, canEdit: false, canVoid: false, canDiscount: false, ...extras };
    case 'edit':
      return { canView: true, canEdit: true, canVoid: false, canDiscount: false, ...extras };
    case 'edit-void':
      return { canView: true, canEdit: true, canVoid: true, canDiscount: false, ...extras };
    case 'edit-void-discount':
    case 'full':
      return { canView: true, canEdit: true, canVoid: true, canDiscount: true, ...extras };
    default:
      return {
        canView: false,
        canEdit: false,
        canVoid: false,
        canDiscount: false,
        canManageAgenda: false,
        canViewOthers: false,
        actions: {},
      };
  }
}

export function permissionLevelFromTriple(t: PermissionTriple): PermissionLevel {
  if (!t.canView) return 'none';
  if (!t.canEdit) return 'view';
  if (t.canVoid && t.canDiscount) return 'edit-void-discount';
  if (t.canVoid) return 'edit-void';
  return 'edit';
}

interface EmployeesState {
  employees: EmployeeRecord[];
  /**
   * Membresía owner del usuario actual en la org activa (role='owner').
   * `employees` excluye self (Team = invitados); los pickers de atribución
   * anteponen `selfOwner` para que el dueño sea opción ("Vos").
   */
  selfOwner: EmployeeRecord | null;
  employeesLoading: boolean;
  employeesError: string | null;
  setEmployees: (employees: EmployeeRecord[]) => void;
  loadEmployees: (options?: { force?: boolean }) => Promise<void>;
  togglePermission: (params: {
    employeeId: string;
    branchId: string;
    module: ModuleKey;
    field: TogglePermissionField;
  }) => void;
  /** Local-only toggle (detail modal pending save). */
  togglePermissionLocal: (params: {
    employeeId: string;
    branchId: string;
    module: ModuleKey;
    field: TogglePermissionField;
  }) => void;
  setPermissionLevel: (params: {
    employeeId: string;
    branchId: string;
    module: ModuleKey;
    level: PermissionLevel;
  }) => void;
  setPermissionAction: (params: {
    employeeId: string;
    branchId: string;
    module: ModuleKey;
    action: string;
    enabled: boolean;
  }) => void;
  savePendingPermissions: (params: {
    employeeId: string;
    branchId: string;
    modules: ModuleKey[];
  }) => Promise<void>;
}

async function persistPermission(
  employeeId: string,
  branchId: string,
  module: ModuleKey,
  values: PermissionTriple,
  field?: TogglePermissionField
): Promise<void> {
  // Audit semántico lo escribe el RPC set_employee_branch_permissions (Slice 5).
  await updateEmployeePermissionApi(
    employeeId,
    branchId,
    module,
    values.canView,
    values.canEdit,
    values.canVoid,
    values.canDiscount,
    values.canManageAgenda,
    values.canViewOthers,
    values.actions,
    { auditMeta: { field, after: values } }
  );
}

export const useEmployeesStore = create<EmployeesState>((set, get) => ({
  employees: [],
  selfOwner: null,
  employeesLoading: false,
  employeesError: null,
  setEmployees: (employees) => {
    employeesFetchedAt = employees.length > 0 ? Date.now() : 0;
    set(employees.length === 0 ? { employees, selfOwner: null } : { employees });
  },
  loadEmployees: async (options?: { force?: boolean }) => {
    const now = Date.now();
    if (
      !options?.force &&
      (get().employees.length > 0 || get().selfOwner != null) &&
      employeesFetchedAt > 0 &&
      now - employeesFetchedAt < EMPLOYEES_CACHE_MS
    ) {
      return;
    }
    if (loadEmployeesInFlight) {
      if (!options?.force) return loadEmployeesInFlight;
      await loadEmployeesInFlight.catch(() => {});
    }

    loadEmployeesInFlight = (async () => {
      set({ employeesLoading: true, employeesError: null });
      try {
        const [list, selfOwner] = await Promise.all([
          fetchEmployeesApi(),
          fetchSelfOwnerMembership().catch(() => null),
        ]);
        employeesFetchedAt = Date.now();
        set({ employees: list, selfOwner, employeesLoading: false });
      } catch (e: any) {
        const raw = e?.message ?? '';
        const message =
          /could not load bundle|failed to load|network request failed/i.test(raw)
            ? EMPLOYEES_LOAD_ERROR
            : raw || EMPLOYEES_LOAD_ERROR;
        set({ employeesLoading: false, employeesError: message });
      } finally {
        loadEmployeesInFlight = null;
      }
    })();

    return loadEmployeesInFlight;
  },
  togglePermission: ({ employeeId, branchId, module, field }) => {
    const target = get().employees.find((e) => e.id === employeeId);
    if (target && target.isActive === false) {
      return;
    }

    const before = readPermissionTriple(get().employees, employeeId, branchId, module);
    const next = computeToggledPermission(before, field);

    set({ employees: applyPermissionToEmployees(get().employees, employeeId, branchId, module, next) });

    void (async () => {
      try {
        await persistPermission(employeeId, branchId, module, next, field);
      } catch (err: unknown) {
        set({ employees: applyPermissionToEmployees(get().employees, employeeId, branchId, module, before) });
        const msg = mapTeamAccessError(
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : undefined,
          'No se pudo guardar el permiso.'
        );
        showError('Error', msg);
      }
    })();
  },

  togglePermissionLocal: ({ employeeId, branchId, module, field }) => {
    const target = get().employees.find((e) => e.id === employeeId);
    if (target && target.isActive === false) {
      return;
    }
    const before = readPermissionTriple(get().employees, employeeId, branchId, module);
    const next = computeToggledPermission(before, field);
    set({ employees: applyPermissionToEmployees(get().employees, employeeId, branchId, module, next) });
  },

  setPermissionLevel: ({ employeeId, branchId, module, level }) => {
    const target = get().employees.find((e) => e.id === employeeId);
    if (target && target.isActive === false) {
      return;
    }
    const current = readPermissionTriple(get().employees, employeeId, branchId, module);
    const values = permissionTripleFromLevel(level, {
      canManageAgenda: module === 'Agenda' ? current.canManageAgenda : false,
      canViewOthers: current.canViewOthers,
      actions: current.actions,
    });
    set({ employees: applyPermissionToEmployees(get().employees, employeeId, branchId, module, values) });
  },

  setPermissionAction: ({ employeeId, branchId, module, action, enabled }) => {
    const target = get().employees.find((e) => e.id === employeeId);
    if (target && target.isActive === false) {
      return;
    }
    const current = readPermissionTriple(get().employees, employeeId, branchId, module);
    const nextActions = { ...(current.actions ?? {}), [action]: enabled };
    const next: PermissionTriple = {
      ...current,
      canView: enabled || current.canView,
      actions: nextActions,
    };
    set({ employees: applyPermissionToEmployees(get().employees, employeeId, branchId, module, next) });
  },

  savePendingPermissions: async ({ employeeId, branchId, modules }) => {
    try {
      const payload = modules.map((module) => {
        const values = readPermissionTriple(get().employees, employeeId, branchId, module);
        return {
          module,
          canView: values.canView,
          canEdit: values.canEdit,
          canVoid: values.canVoid,
          canDiscount: values.canDiscount,
          canManageAgenda: values.canManageAgenda,
          canViewOthers: values.canViewOthers,
          actions: values.actions,
        };
      });
      await setEmployeeBranchPermissions(employeeId, branchId, payload, {
        auditEvent: 'employee.permission.change',
        auditMeta: { modules, batch: true },
      });
    } catch (err: unknown) {
      presentUnexpectedFailure({ module: 'team', op: 'permissions.save', error: err, userMessage: 'No se pudo guardar el permiso.' });
      throw err;
    }
  },
}));
